import {config, Map, MapStyle, MaptilerNavigationControl, Marker} from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';

class Mapbox {
    // Constantes de configuración
    static DEFAULTS = {
        CLUSTER_MAX_ZOOM: 15,
        CLUSTER_RADIUS: 30,
        CIRCLE_RADIUS: 15,
        STROKE_WIDTH: 1.5,
        HIGHLIGHT_WIDTH: 5,
        DEFAULT_STROKE_WIDTH: 1.2,
        PRIMARY_COLOR: '#3B82F6',
        CIRCLE_COLOR: 'white',
        TEXT_SIZE: 14,
        MAP_STYLE: MapStyle.STREETS,
        HALO_WIDTH: 1.5
    };

    constructor(token, options = {}) {
        this.map = null;
        this.center = null;
        this.polygon = null;
        this.bbox = null;
        this.markers = [];
        this.token = token;
        this.eventHandlers = {};
        this.debug = options.debug || false;

        // Configuración de colores personalizable
        this.colors = {
            primary: options.primaryColor || Mapbox.DEFAULTS.PRIMARY_COLOR,
            markerColor: options.markerColor || Mapbox.DEFAULTS.PRIMARY_COLOR,
            textColor: options.textColor || Mapbox.DEFAULTS.PRIMARY_COLOR,
            circleColor: options.circleColor || Mapbox.DEFAULTS.CIRCLE_COLOR,
            haloColor: options.haloColor || '#FFFFFF'
        };

        config.apiKey = token;
    }

    // Método de logging para debugging
    log(...args) {
        if (this.debug) {
        }
    }

    // Método de warning para debugging
    warn(...args) {
        if (this.debug) {
            console.warn('[Mapbox]', ...args);
        }
    }

    // Esperar a que el mapa esté cargado
    waitForMapLoad() {
        return new Promise((resolve) => {
            if (this.map?.loaded()) {
                resolve();
            } else if (this.map) {
                this.map.once('load', resolve);
            } else {
                resolve();
            }
        });
    }

    resize() {
        if (!this.map) return;

        setTimeout(() => {
            this.map.resize({ programmatic: true });
        }, 1);
    }

    makeMap(input, options, controls = {}) {
        const { style: customStyle, ...restOptions } = options;

        this.map = new Map({
            container: input,
            language: 'local',
            attributionControl: false,
            navigationControl: false,
            geolocateControl: false,
            style: customStyle || Mapbox.DEFAULTS.MAP_STYLE,
            ...restOptions
        });

        const defaultControl = {
            showCompass: false,
            showZoom: true,
        };

        this.map.addControl(
            new MaptilerNavigationControl({
                ...defaultControl,
                ...controls,
            })
        );

        this.center = options.center;

        // Configurar event handlers
        this.setupEventHandlers();

        this.log('Map initialized', { center: this.center });
    }

    // Configurar todos los event handlers
    setupEventHandlers() {
        // Handler para load
        this.eventHandlers.load = () => {
            this.calculatePolygonBboxAndCenter();
            this.log('Map loaded', { center: this.center, bbox: this.bbox });

            this.dispatchCustomEvent("mapbox-load", {
                center: this.center,
                polygon: this.polygon,
                bbox: this.bbox,
            });
        };

        // Handler para click genérico del mapa (solo cuando NO es cluster ni unclustered)
        this.eventHandlers.click = (e) => {
            // Verificar si el click fue en un cluster o unclustered
            const clusterFeatures = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const unclusteredFeatures = this.map.queryRenderedFeatures(e.point, { layers: ['unclustered'] });

            // Solo disparar el evento si NO se hizo click en un cluster o marcador
            if (clusterFeatures.length === 0 && unclusteredFeatures.length === 0) {
                this.dispatchCustomEvent("toogleOpenClusters", { show: false });
                this.resetLayerStyles();
            }
        };

        // Handler para movestart
        this.eventHandlers.movestart = () => {
            this.dispatchCustomEvent("toogleOpenClusters", { show: false });
            this.resetLayerStyles();
        };

    // Handler para moveend
    this.eventHandlers.moveend = (eventData) => {
        // Ignorar eventos de resize para evitar búsquedas innecesarias
        if (eventData?.originalEvent?.type === 'resize') {
            return;
        }

        this.calculatePolygonBboxAndCenter();
        const zoom = this.map.getZoom();
        const isProgrammatic = !!(eventData.programmatic || (eventData.originalEvent && eventData.originalEvent.programmatic));

        // DIAGNÓSTICO: Rastrear quién emite este evento
        if (this.debug) {
            console.group(`📡 Mapbox moveend - Programmatic: ${isProgrammatic}`);
            console.log('Event Data:', eventData);
            console.trace();
            console.groupEnd();
        }

        this.dispatchCustomEvent("mapbox-moveend", {
            center: this.center,
            polygon: this.polygon,
            bbox: this.bbox,
            slug: eventData.slug ?? undefined,
            locationName: eventData.locationName ?? undefined,
            programmatic: isProgrammatic,
            source: eventData.source || null,
            forceSearch: !!eventData.forceSearch
        });

        this.dispatchCustomEvent("changed_zoom", { zoom, programmatic: isProgrammatic });
        localStorage.setItem("zoom", zoom);
    };

    // Handler para cluster click
    this.eventHandlers.clusterClick = (e) => {
            this.handleClusterClick(e);
        };

        // Handler para unclustered click
        this.eventHandlers.unclusteredClick = (e) => {
            this.handleUnclusteredClick(e);
        };

        // Registrar event handlers
        this.map.on("load", this.eventHandlers.load);
        this.map.on("click", this.eventHandlers.click);
        this.map.on("movestart", this.eventHandlers.movestart);
        this.map.on("moveend", this.eventHandlers.moveend);
        this.map.on("click", "clusters", this.eventHandlers.clusterClick);
        this.map.on("click", "unclustered", this.eventHandlers.unclusteredClick);
    }

    // Verificar si debe ignorar el evento moveend
    shouldSkipMoveEnd(eventData) {
        return (
            (eventData?.originalEvent?.type === 'resize')
        );
    }

    // Manejar click en cluster
    handleClusterClick(e) {
        if (document.startViewTransition) {
            document.startViewTransition(() => {
                this.dispatchCustomEvent("toogleOpenClusters", { show: true });
            });
        } else {
            this.dispatchCustomEvent("toogleOpenClusters", { show: true });
        }

        const features = this.map.queryRenderedFeatures(e.point, {
            layers: ["clusters"],
        });

        if (!features || features.length === 0) {
            this.warn('No cluster features found at click point');
            return;
        }

        const clusterId = features[0].properties.cluster_id;
        const point_count = features[0].properties.point_count;

        this.log('Cluster clicked', { clusterId, point_count });

        // Resaltar el cluster seleccionado
        this.highlightLayer("clusters", "cluster_id", clusterId);
        this.resetLayerStyle("unclustered");

        const clusterSource = this.map.getSource("buildings");

        if (!clusterSource) {
            console.error('❌ No cluster source found!');
            return;
        }

        // MapTiler SDK usa Promises, no callbacks
        clusterSource.getClusterLeaves(clusterId, point_count, 0)
            .then((buildings) => {
                if (!buildings || buildings.length === 0) {
                    this.warn('No buildings found in cluster');
                    return;
                }

                const filter_buildings = buildings.map(
                    building => building.properties.building_id
                );

                this.log('Cluster buildings', filter_buildings);

                if (document.startViewTransition) {
                    document.startViewTransition(() => {
                        this.dispatchCustomEvent("cluster", {
                            ids: filter_buildings,
                        });
                    });
                } else {
                    this.dispatchCustomEvent("cluster", {
                        ids: filter_buildings,
                    });
                }
            })
            .catch((err) => {
                console.error('❌ Error getting cluster leaves:', err);
            });
    }

    // Manejar click en punto sin cluster
    handleUnclusteredClick(e) {
        if (document.startViewTransition) {
            document.startViewTransition(() => {
                this.dispatchCustomEvent("toogleOpenClusters", { show: true });
            });
        } else {
            this.dispatchCustomEvent("toogleOpenClusters", { show: true });
        }

        const features = this.map.queryRenderedFeatures(e.point, {
            layers: ["unclustered"],
        });

        if (!features || features.length === 0) {
            this.warn('No unclustered features found at click point');
            return;
        }

        const buildingId = features[0].properties.building_id;

        this.log('Unclustered point clicked', { buildingId });

        this.highlightLayer("unclustered", "building_id", buildingId);
        this.resetLayerStyle("clusters");

        if (document.startViewTransition) {
            document.startViewTransition(() => {
                this.dispatchCustomEvent("unclustered", {
                    ids: [buildingId],
                });
            });
        } else {
            this.dispatchCustomEvent("unclustered", {
                ids: [buildingId],
            });
        }

        const element = document.getElementById("cluster_section");
        if (element) {
            if (document.startViewTransition) {
                document.startViewTransition(() => {
                    element.classList.remove("hidden");
                    element.classList.add("block");
                });
            } else {
                element.classList.remove("hidden");
                element.classList.add("block");
            }
        }
    }

    // Despachar evento personalizado
    dispatchCustomEvent(eventName, detail) {
        const event = new CustomEvent(eventName, { detail });
        window.dispatchEvent(event);
    }

    // Resetear estilos de todas las capas
    resetLayerStyles() {
        this.resetLayerStyle("unclustered");
        this.resetLayerStyle("clusters");
    }

    // Resetear estilo de una capa específica
    resetLayerStyle(layerName) {
        if (typeof this.map.getLayer(layerName) !== "undefined") {
            this.map.setPaintProperty(
                layerName,
                "circle-stroke-width",
                Mapbox.DEFAULTS.DEFAULT_STROKE_WIDTH
            );
        }
    }

    // Resaltar una capa específica
    highlightLayer(
        layerName,
        property,
        value,
        highlightWidth = Mapbox.DEFAULTS.HIGHLIGHT_WIDTH,
        defaultWidth = Mapbox.DEFAULTS.DEFAULT_STROKE_WIDTH
    ) {
        if (typeof this.map.getLayer(layerName) !== "undefined") {
            this.map.setPaintProperty(layerName, "circle-stroke-width", [
                "match",
                ["get", property],
                value,
                highlightWidth,
                defaultWidth,
            ]);
        }
    }

    addMarker(options, deleteMarkers = false) {
        const markerOptions = options.element
            ? { element: options.element, anchor: options.anchor || 'center' }
            : { color: this.colors.markerColor };

        const marker = new Marker(markerOptions)
        .setLngLat(options.center)
        .addTo(this.map);

        if (deleteMarkers === true) {
            this.markers.forEach((marker) => marker.remove());
            this.markers = [];
        }

        this.markers.push(marker);
        this.log('Marker added', { center: options.center, totalMarkers: this.markers.length });
    }

    async dataToMap(buildings) {
        if (!this.map) return;

        // Validar que buildings sea válido
        if (!buildings?.features) {
            this.warn('Invalid GeoJSON data provided to dataToMap');
            return;
        }

        this.log('Data to map', {
            total: buildings.features.length
        });

        if (this.map.getSource("buildings") === undefined) {
            this.setLayers(buildings);
        } else {
            this.map.getSource("buildings").setData(buildings);
        }
    }

    setLayers(data) {
        this.map.addSource("buildings", {
            type: "geojson",
            data: data,
            cluster: true,
            clusterMaxZoom: Mapbox.DEFAULTS.CLUSTER_MAX_ZOOM,
            clusterRadius: Mapbox.DEFAULTS.CLUSTER_RADIUS,
            clusterProperties: {
                units: ["+", ["get", "units"]],
            },
        });

        // Layer: Círculos de clusters
        this.map.addLayer({
            id: "clusters",
            type: "circle",
            source: "buildings",
            filter: ["all",
                ["has", "point_count"],
                [">", ["get", "units"], 0]
            ],
            paint: {
                "circle-color": this.colors.circleColor,
                "circle-radius": Mapbox.DEFAULTS.CIRCLE_RADIUS,
                "circle-stroke-width": Mapbox.DEFAULTS.STROKE_WIDTH,
                "circle-stroke-color": this.colors.primary,
                "circle-stroke-opacity": 1,
            },
        });

        // Layer: Texto de clusters
        this.map.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: "buildings",
            filter: ["all",
                ["has", "point_count"],
                [">", ["get", "units"], 0]
            ],
            layout: {
                "text-field": [
                    "number-format",
                    ["get", "units"],
                    { "min-fraction-digits": 0, "max-fraction-digits": 0 },
                ],
                "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                "text-size": Mapbox.DEFAULTS.TEXT_SIZE,
                "text-allow-overlap": true,
                "text-ignore-placement": true,
            },
            paint: {
                "text-color": this.colors.textColor,
                "text-halo-color": this.colors.haloColor,
                "text-halo-width": Mapbox.DEFAULTS.HALO_WIDTH,
            },
        });

        // Layer: Círculos sin cluster
        this.map.addLayer({
            id: "unclustered",
            type: "circle",
            source: "buildings",
            filter: ["all",
                ["!", ["has", "point_count"]],
                [">", ["get", "units"], 0]
            ],
            paint: {
                "circle-color": this.colors.circleColor,
                "circle-radius": Mapbox.DEFAULTS.CIRCLE_RADIUS,
                "circle-stroke-width": Mapbox.DEFAULTS.STROKE_WIDTH,
                "circle-stroke-color": this.colors.primary,
                "circle-stroke-opacity": 1,
            },
        });

        // Layer: Texto de puntos sin cluster
        this.map.addLayer({
            id: "unclustered-point",
            type: "symbol",
            source: "buildings",
            filter: ["all",
                ["!", ["has", "point_count"]],
                [">", ["get", "units"], 0]
            ],
            layout: {
                "text-field": [
                    "number-format",
                    ["get", "units"],
                    { "min-fraction-digits": 0, "max-fraction-digits": 0 },
                ],
                "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                "text-size": Mapbox.DEFAULTS.TEXT_SIZE,
                "text-allow-overlap": true,
                "text-ignore-placement": true,
            },
            paint: {
                "text-color": this.colors.textColor,
                "text-halo-color": this.colors.haloColor,
                "text-halo-width": Mapbox.DEFAULTS.HALO_WIDTH,
            },
        });

        this.log('Layers set successfully');
    }

    // Actualizar opciones de clustering dinámicamente
    updateClusterOptions(options = {}) {
        const source = this.map.getSource("buildings");
        if (!source) {
            this.warn('No buildings source found to update');
            return;
        }

        const currentData = source._data;

        // Remover capas y source existente
        ["unclustered-point", "unclustered", "cluster-count", "clusters"].forEach(
            (layerId) => {
                if (this.map.getLayer(layerId)) {
                    this.map.removeLayer(layerId);
                }
            }
        );

        this.map.removeSource("buildings");

        // Recrear source con nuevas opciones
        this.map.addSource("buildings", {
            type: "geojson",
            data: currentData,
            cluster: true,
            clusterMaxZoom: options.clusterMaxZoom || Mapbox.DEFAULTS.CLUSTER_MAX_ZOOM,
            clusterRadius: options.clusterRadius || Mapbox.DEFAULTS.CLUSTER_RADIUS,
            clusterProperties: {
                units: ["+", ["get", "units"]],
            },
        });

        this.setLayers(currentData);
        this.log('Cluster options updated', options);
    }

    calculatePolygonBboxAndCenter() {
        const temp = this.map.getCenter();
        this.center = [temp.lng, temp.lat];

        const bounds = this.map.getBounds().toArray();
        let bbox = [];

        bounds.forEach((element) => (bbox = bbox.concat(element)));
        this.bbox = bbox;

        this.polygon = {
            type: "Feature",
            bbox: this.bbox,
            properties: {},
            geometry: {
                type: "Polygon",
                coordinates: [
                    [
                        [this.bbox[0], this.bbox[1]],
                        [this.bbox[0], this.bbox[3]],
                        [this.bbox[2], this.bbox[3]],
                        [this.bbox[2], this.bbox[1]],
                        [this.bbox[0], this.bbox[1]],
                    ],
                ],
            },
        };
    }

    // Limpiar y destruir el mapa
    destroy() {
        if (!this.map) return;

        this.log('Destroying map');

        // Remover event listeners
        if (this.eventHandlers.load) {
            this.map.off("load", this.eventHandlers.load);
        }
        if (this.eventHandlers.click) {
            this.map.off("click", this.eventHandlers.click);
        }
        if (this.eventHandlers.movestart) {
            this.map.off("movestart", this.eventHandlers.movestart);
        }
        if (this.eventHandlers.moveend) {
            this.map.off("moveend", this.eventHandlers.moveend);
        }
        if (this.eventHandlers.clusterClick) {
            this.map.off("click", "clusters", this.eventHandlers.clusterClick);
        }
        if (this.eventHandlers.unclusteredClick) {
            this.map.off("click", "unclustered", this.eventHandlers.unclusteredClick);
        }

        // Remover marcadores
        this.markers.forEach((marker) => marker.remove());
        this.markers = [];

        // Destruir mapa
        this.map.remove();
        this.map = null;
        this.eventHandlers = {};

        this.log('Map destroyed');
    }
}

export default Mapbox;
